import React from 'react'
import { ShieldCheck, ArrowUpRight } from 'lucide-react'
import Reveal from './Reveal'
import TiltCard from './TiltCard'
import Atmosphere from './Atmosphere'
import { usePrefersReducedMotion } from '../hook/usePrefersReducedMotion'

const tools = ['Suite Adobe', 'Blender', 'Unity', 'Unreal Engine', 'Motion capture', 'IA générative']

const IntroSection = () => {
  const reduced = usePrefersReducedMotion()
  const openContact = () => window.dispatchEvent(new CustomEvent('pdv:open-contact'))

  return (
    <section className="relative overflow-hidden py-24 lg:py-32">
      <Atmosphere variant="calm" />

      <div className="container-pdv relative z-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Texte */}
          <Reveal>
            <span className="eyebrow">Le studio</span>
            <h2 className="mt-5 font-display font-extrabold leading-[1.04] text-[clamp(2rem,4.2vw,3.4rem)]">
              La création numérique,
              <span className="text-gradient"> autrement.</span>
            </h2>

            <p className="mt-7 text-lg text-[#cdc1ee] leading-relaxed">
              <strong className="text-white">Place des Victoires</strong> est un studio de création
              multimédia en <strong className="text-white">Entreprise Adaptée</strong>, opéré par des
              personnes en situation de handicap ayant développé des{' '}
              <strong className="text-white">« <span lang="en">Mad Skills</span> »</strong> créatifs : des traits de caractère
              atypiques et décalés, sources d'idées inattendues.
            </p>
            <p className="mt-5 text-lg text-[#b6a9d8] leading-relaxed">
              Spécialisés dans l'accessibilité de la création numérique, nous maîtrisons les outils
              professionnels et les nouvelles technologies : <span lang="en">motion capture</span>, animation 3D et
              intelligence artificielle générative.
            </p>

            <ul className="mt-8 flex flex-wrap gap-2.5">
              {tools.map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-sm text-[#cbbef0]"
                >
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Visuel 3D */}
          <Reveal delay={120} className="relative">
            <TiltCard className="card-surface ring-gradient rounded-[1.8rem] p-3 shadow-glow mx-auto max-w-md" max={6}>
              <div className="relative overflow-hidden rounded-[1.4rem]">
                <video autoPlay={!reduced} muted loop playsInline aria-hidden="true" className="h-[26rem] w-full object-cover">
                  <source src="/image place des victoires/turn_Antoine.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0a0218]/90 to-transparent" />
                <p className="absolute bottom-4 left-4 font-display font-bold text-white">
                  Personnage 3D · création interne
                </p>
              </div>
            </TiltCard>

            <div className="absolute -bottom-5 -left-3 sm:-left-6 glass rounded-2xl px-5 py-3 shadow-card">
              <p className="text-2xl font-display font-extrabold text-gradient-soft">100%</p>
              <p className="text-xs text-[#bcaee6]">réalisé par nos talents</p>
            </div>
          </Reveal>
        </div>

        {/* Bandeau Entreprise Adaptée / AGEFIPH */}
        <Reveal delay={80}>
          <div className="mt-20 card-surface ring-gradient rounded-3xl p-8 sm:p-10 lg:p-12 relative overflow-hidden">
            <div
              className="absolute inset-0 -z-10 opacity-60"
              style={{ background: 'radial-gradient(70% 120% at 100% 0%, rgba(138,43,226,0.25), transparent 60%)' }}
            />
            <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
              <div className="flex-1">
                <div className="inline-flex items-center gap-3 mb-4">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-grad-violet shadow-glow">
                    <ShieldCheck size={22} className="text-white" />
                  </span>
                  <span className="font-display font-bold text-xl">Un partenaire à impact</span>
                </div>
                <p className="text-lg text-[#cdc1ee] leading-relaxed max-w-3xl">
                  Grâce à son agrément <strong className="text-white">Entreprise Adaptée</strong>, Place des
                  Victoires permet aux entreprises de <strong className="text-white">+20 salariés</strong> de
                  respecter leurs obligations d'emploi de travailleurs handicapés tout en{' '}
                  <strong className="text-white">réduisant une partie de leur taxe AGEFIPH</strong>.
                </p>
              </div>
              <button onClick={openContact} className="btn btn-primary shrink-0 group">
                Devenir partenaire
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

export default IntroSection
